/**
 * Footer Component
 * 
 * Enterprise-grade footer with legal links including cookie settings
 * Apple-inspired minimalist design in Danish
 * WCAG 2.1 AA compliant with keyboard navigation
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { CookieModal } from './cookies/CookieModal';

export function Footer() {
  const [showCookieModal, setShowCookieModal] = useState(false);

  return (
    <footer className="hidden md:block bg-app-surface py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Company Info */}
          <div className="space-y-3">
            <div className="flex">
              <img 
                src="/tryg-hand-footer.png" 
                alt="Tryg Hånd ApS" 
                className="h-24 w-auto ml-12"
                onError={(e) => {
                  // Fallback to SVG logo if PNG fails to load
                  (e.target as HTMLImageElement).src = '/logo.svg';
                }}
              />
            </div>
            <p className="text-xs text-app-text-secondary">
              Vikarbureau specialiseret i børn og unge
            </p>
            <div className="text-xs text-app-text-tertiary space-y-1">
              <p>Formervangen 18, 2600 Glostrup</p>
              <p>CVR: 45915433</p>
              <p>GLN/EAN: 5790002852985</p>
            </div>
          </div>

          {/* Legal Links */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-app-text">Juridisk</h3>
            <nav aria-label="Juridiske links" className="flex flex-col space-y-2">
              <Link
                to="/privatlivspolitik"
                className="text-xs text-app-text-secondary hover:text-app-text transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-focus focus-visible:ring-offset-2 rounded inline-block"
              >
                Privatlivspolitik
              </Link>
              <Link
                to="/cookies"
                className="text-xs text-app-text-secondary hover:text-app-text transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-focus focus-visible:ring-offset-2 rounded inline-block"
              >
                Cookiepolitik
              </Link>
              <Link
                to="/terms"
                className="text-xs text-app-text-secondary hover:text-app-text transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-focus focus-visible:ring-offset-2 rounded inline-block"
              >
                Servicevilkår
              </Link>
              <button
                onClick={() => {
                  console.log('Cookie settings clicked! Opening modal...');
                  setShowCookieModal(true);
                }}
                className="text-xs text-app-text-secondary hover:text-app-text transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-focus focus-visible:ring-offset-2 rounded text-left inline-flex items-center gap-1.5"
                aria-label="Åbn cookieindstillinger"
                type="button"
              >
                <Settings className="h-3 w-3" aria-hidden="true" />
                Cookieindstillinger
              </button>
            </nav>
          </div>

          {/* Contact */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-app-text">Kontakt</h3>
            <div className="text-xs text-app-text-secondary space-y-1">
              <p>
                <a
                  href="mailto:kontakt@tryghaand.dk"
                  className="hover:text-app-text transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-focus focus-visible:ring-offset-2 rounded inline-block"
                >
                  kontakt@tryghaand.dk
                </a>
              </p>
              <p>
                <a
                  href="tel:+4571717154"
                  className="hover:text-app-text transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-focus focus-visible:ring-offset-2 rounded inline-block"
                >
                  +45 71 71 71 54
                </a>
              </p>
              <p className="mt-2 pt-2 border-t border-app-border">
                <strong className="text-app-text block">Databeskyttelse:</strong>
                <a
                  href="mailto:it@tryghaand.dk"
                  className="hover:text-app-text transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-focus focus-visible:ring-offset-2 rounded inline-block"
                >
                  it@tryghaand.dk
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <p className="text-xs text-app-text-tertiary">
                Tryg Hånd &copy; {new Date().getFullYear()} – Alle rettigheder forbeholdes
              </p>
              <span className="hidden sm:inline text-xs text-app-text-tertiary">•</span>
              <p className="text-xs text-app-text-tertiary">
                Udviklet i Danmark
              </p>
            </div>
            <p className="text-xs text-app-text-tertiary">
              <a
                href="https://nwstudios.dk"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-app-text transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-focus focus-visible:ring-offset-1 rounded"
              >
                Udviklet af NW Studios
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Cookie Settings Modal */}
      <CookieModal 
        open={showCookieModal} 
        onClose={() => setShowCookieModal(false)} 
      />
    </footer>
  );
}

